import { useCallback, useRef, useState } from 'react';
import Daily, { DailyCall } from '@daily-co/daily-js';
import { makeLog } from 'shared/utils/log';
import { mediaDevices } from 'shared/utils/media-devices';
import { VideoParticipantType, VideoParticipantsWithUserDataObject } from 'types/video';
import { useAppDispatch } from 'shared/store';
import {
  setScreenShareInFocus,
  setScreenSharing,
  unsetScreenShareInFocus,
} from 'shared/store/salesfloor-slice';

const log = makeLog('video-room');

export interface VideoRoomParticipant {
  id: string;
  userId: string;
  name: string;
  audioTrack?: MediaStreamTrack;
  videoTrack?: MediaStreamTrack;
  screenVideoTrack?: MediaStreamTrack;
  avStream?: MediaStream;
}

type VideoRoomStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface VideoRoom {
  id: string;
  localParticipant: VideoRoomParticipant;
  remoteParticipants: VideoRoomParticipant[];
  status: VideoRoomStatus;
  isConnected: boolean;
}
export interface VideoRoomManager {
  videoRoom: VideoRoom | null;
  join: (
    url: string,
    token: string,
    participantName: string,
    userData?: any,
  ) => Promise<void>;
  leave: () => Promise<void>;
  acquireStream: (
    preferredAudioDeviceId: string | boolean,
    preferredVideoDeviceId: string | boolean,
  ) => Promise<void>;
  setLocalAudioTrack: (track: MediaStreamTrack | null) => Promise<void>;
  setLocalExtraAudioTrack: (track: MediaStreamTrack | null) => Promise<void>;
  setLocalVideoTrack: (track: MediaStreamTrack | null) => Promise<void>;
  setLocalAudio: (enabled: boolean) => void;
  setLocalVideo: (enabled: boolean) => void;
  isLocalAudioEnabled: boolean;
  isLocalVideoEnabled: boolean;
  localAvStream: MediaStream | null;
  dailyCall: DailyCall | null;
  activeSpeaker?: string | null;
}

export const DEFAULT_VIDEO_ROOM_MANAGER = {
  videoRoom: null,
  join: async () => {},
  leave: async () => {},
  acquireStream: async () => {},
  setLocalExtraAudioTrack: async () => {},
  setLocalAudioTrack: async () => {},
  setLocalVideoTrack: async () => {},
  setLocalAudio: () => {},
  setLocalVideo: () => {},
  isLocalAudioEnabled: false,
  isLocalVideoEnabled: false,
  localAvStream: null,
  dailyCall: null,
};

export function isTrackEnabled(track: MediaStreamTrack | null) {
  return track ? track.enabled && !track.muted : false;
}

export function mixTracks(track1: MediaStreamTrack, track2: MediaStreamTrack) {
  const ctx = new AudioContext();
  const dest = ctx.createMediaStreamDestination();
  const source1 = ctx.createMediaStreamSource(new MediaStream([track1]));
  const source2 = ctx.createMediaStreamSource(new MediaStream([track2]));
  source1.connect(dest);
  source2.connect(dest);
  return dest.stream.getAudioTracks()[0];
}

function listenForTrackEnabledChange(
  track: MediaStreamTrack | null,
  callback: (enabled: boolean) => void,
) {
  if (!track) {
    return;
  }

  const handleEnabledChange = () => {
    callback(isTrackEnabled(track));
  };

  track.addEventListener('ended', handleEnabledChange);
  track.addEventListener('mute', handleEnabledChange);
  track.addEventListener('unmute', handleEnabledChange);
  return () => {
    track.removeEventListener('ended', handleEnabledChange);
    track.removeEventListener('mute', handleEnabledChange);
    track.removeEventListener('unmute', handleEnabledChange);
  };
}

function addTrackToStream(
  stream: MediaStream,
  track: MediaStreamTrack | null,
  kind: 'audio' | 'video',
) {
  let oldTracks: MediaStreamTrack[];
  if (kind === 'audio') {
    oldTracks = stream.getAudioTracks();
  } else {
    oldTracks = stream.getVideoTracks();
  }

  if (track) {
    const streamHasTrack = oldTracks.some(t => t.id === track.id);
    if (streamHasTrack) {
      log.debug(`Stream already has ${kind} track ${track.id}`);
      return;
    }
    log.debug(`Adding ${kind} track ${track.id} to stream`);
    stream.addTrack(track);
  }

  oldTracks.forEach(t => {
    log.debug(`Removing old ${kind} track ${t.id} from stream`);
    stream.removeTrack(t);
  });
}

function getVideoRoomStatus(call: DailyCall | null): VideoRoomStatus {
  if (!call) {
    return 'disconnected';
  }

  switch (call.meetingState()) {
    case 'new':
    case 'loading':
    case 'loaded':
    case 'joining-meeting':
      return 'connecting';
    case 'joined-meeting':
      return 'connected';
    case 'left-meeting':
      return 'disconnected';
    case 'error':
      return 'error';
    default:
      return 'disconnected';
  }
}

function getIsConnected(status: VideoRoomStatus) {
  return status === 'connected';
}

// click on the screen share button
// all should see the screen share
// can navigate to

export function useVideoRoomManager(): VideoRoomManager {
  const dispatch = useAppDispatch();
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);
  const extraAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const audioTrackEnabledListener = useRef<() => void>();
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const videoTrackEnabledListener = useRef<() => void>();
  const localAvStreamRef = useRef<MediaStream | null>(null);
  const remoteAvStreamMapRef = useRef<{ [id: string]: MediaStream }>({});
  const dailyCallRef = useRef<DailyCall | null>(null);
  const stopAllRef = useRef<(() => void) | null>(null);
  const [isLocalAudioEnabled, setIsLocalAudioEnabled] = useState<boolean>(false);
  const [isLocalVideoEnabled, setIsLocalVideoEnabled] = useState<boolean>(false);
  const [videoRoom, setVideoRoom] = useState<VideoRoom | null>(null);
  const [activeSpeaker, setActiveSpeaker] = useState<string | null>();

  const publishAudio = async () => {
    log.debug('Publishing audio');
    let track: MediaStreamTrack | boolean = false;
    if (audioTrackRef.current && extraAudioTrackRef.current) {
      log.debug('Mixing audio tracks');
      track = mixTracks(audioTrackRef.current, extraAudioTrackRef.current);
    } else if (audioTrackRef.current) {
      log.debug('Using audio track');
      track = audioTrackRef.current;
    }

    if (dailyCallRef.current) {
      await dailyCallRef.current?.setInputDevicesAsync({ audioSource: track });
    }
  };

  const setLocalAudioTrack = useCallback(async (track: MediaStreamTrack | null) => {
    log.debug(`Setting local audio track ${track?.id}`);
    if (audioTrackRef.current !== track) {
      audioTrackEnabledListener.current?.();
      audioTrackRef.current?.stop();
      audioTrackRef.current = track;
      audioTrackEnabledListener.current = listenForTrackEnabledChange(
        track,
        setIsLocalAudioEnabled,
      );
      setIsLocalAudioEnabled(isTrackEnabled(track));
      if (!localAvStreamRef.current) {
        localAvStreamRef.current = new MediaStream();
        log.debug(`Created new localAvStreamRef ${localAvStreamRef.current.id}`);
      }
      addTrackToStream(localAvStreamRef.current, track, 'audio');
      log.debug(
        `Added local audio track ${track?.id} to localAvStreamRef ${localAvStreamRef.current.id}`,
      );

      await publishAudio();
    } else {
      log.debug(`Local audio track ${track?.id} already set`);
    }

    if (dailyCallRef.current) {
      dailyCallRef.current.setLocalAudio(!!track);
    }
  }, []);

  const setLocalExtraAudioTrack = useCallback(async (track: MediaStreamTrack | null) => {
    log.debug(`Setting local extra audio track ${track?.id}`);
    if (extraAudioTrackRef.current !== track) {
      extraAudioTrackRef.current = track;
      await publishAudio();
    } else {
      log.debug(`Local extra audio track ${track?.id} already set`);
    }
  }, []);

  const setLocalVideoTrack = useCallback(async (track: MediaStreamTrack | null) => {
    log.debug(
      `Setting local video track ${track?.id} enabled=${track?.enabled} muted=${track?.muted}`,
    );

    if (dailyCallRef.current) {
      dailyCallRef.current.setLocalVideo(!!track);
    }

    if (videoTrackRef.current !== track) {
      videoTrackEnabledListener.current?.();

      videoTrackRef.current = track;
      videoTrackEnabledListener.current = listenForTrackEnabledChange(
        track,
        setIsLocalVideoEnabled,
      );
      setIsLocalVideoEnabled(isTrackEnabled(track));
      if (!localAvStreamRef.current) {
        localAvStreamRef.current = new MediaStream();
        log.debug(`Created new localAvStreamRef ${localAvStreamRef.current.id}`);
      }
      addTrackToStream(localAvStreamRef.current, track, 'video');
      log.debug(
        `Added local video track ${track?.id} to localAvStreamRef ${localAvStreamRef.current.id}`,
      );
    } else {
      log.debug(`Local video track ${track?.id} already set`);
    }
  }, []);

  const handleLocalAudioTrackChange = useCallback((val: boolean) => {
    if (!audioTrackRef.current) {
      log.debug('No audio track to enable/disable');
      return;
    }
    audioTrackRef.current.enabled = val;
    setIsLocalAudioEnabled(val);
    log.debug(`Set local audio track ${audioTrackRef.current.id} enabled=${val}`);

    if (dailyCallRef.current) {
      dailyCallRef.current.setLocalAudio(val);
    }
  }, []);

  const handleLocalVideoTrackChange = useCallback((val: boolean) => {
    if (!videoTrackRef.current) {
      log.debug('No video track to enable/disable');
      return;
    }
    videoTrackRef.current.enabled = val;
    setIsLocalVideoEnabled(val);
    log.debug(`Set local video track ${videoTrackRef.current.id} enabled=${val}`);

    if (dailyCallRef.current) {
      dailyCallRef.current.setLocalVideo(val);
    }
  }, []);

  const acquireStream = useCallback(
    async (
      preferredAudioDeviceId: string | boolean = true,
      preferredVideoDeviceId: string | boolean = true,
    ) => {
      return mediaDevices
        .setupCameraMediaStream({
          audio: preferredAudioDeviceId,
          video: preferredVideoDeviceId,
        })
        .then(res => {
          void setLocalVideoTrack(res.stream.getVideoTracks()[0]);
          void setLocalAudioTrack(res.stream.getAudioTracks()[0]);
          stopAllRef.current = res.stopAll;
        });
    },
    [setLocalAudioTrack, setLocalVideoTrack],
  );

  const updateRoom = () => {
    const call = dailyCallRef.current;
    if (!call) {
      log.info('No call to update room');
      return;
    }

    const participants = call.participants() as VideoParticipantsWithUserDataObject;
    log.debug('Participants', JSON.stringify(participants));
    const localParticipant = participants['local'];
    if (!participants['local']) {
      return;
    }
    const remoteParticipants = Object.entries(participants).filter(
      ([id, p]) =>
        id !== 'local' && p.userData?.participantType !== VideoParticipantType.LIVELISTEN,
    );

    Object.keys(remoteAvStreamMapRef).forEach(id => {
      if (!participants[id]) {
        delete remoteAvStreamMapRef.current[id];
      }
    });

    remoteParticipants.forEach(([_, p]) => {
      if (!remoteAvStreamMapRef.current[p.session_id]) {
        remoteAvStreamMapRef.current[p.session_id] = new MediaStream();
      }

      const remoteAudioTrack = p.tracks.audio.track || null;
      const remoteVideoTrack = p.tracks.video.track || null;
      addTrackToStream(
        remoteAvStreamMapRef.current[p.session_id],
        remoteAudioTrack,
        'audio',
      );
      addTrackToStream(
        remoteAvStreamMapRef.current[p.session_id],
        remoteVideoTrack,
        'video',
      );
    });

    const status = getVideoRoomStatus(call);

    setVideoRoom({
      id: call.room.name,
      status: status,
      isConnected: getIsConnected(status),
      localParticipant: {
        id: localParticipant.session_id,
        userId: localParticipant.user_id,
        name: localParticipant.user_name,
        audioTrack: audioTrackRef.current || undefined,
        videoTrack: videoTrackRef.current || undefined,
        avStream: localAvStreamRef.current || undefined,
        screenVideoTrack: localParticipant.tracks.screenVideo.track || undefined,
      },
      remoteParticipants: Object.entries(participants)
        .filter(
          ([id, p]) =>
            id !== 'local' &&
            p.userData?.participantType !== VideoParticipantType.LIVELISTEN,
        )
        .map(([_, p]) => {
          return {
            id: p.session_id,
            userId: p.user_id,
            name: p.user_name,
            audioTrack: p.tracks.audio.track,
            videoTrack: p.tracks.video.track,
            screenVideoTrack: p.tracks.screenVideo.track,
            avStream: remoteAvStreamMapRef.current[p.session_id],
          };
        }),
    });
  };

  const disconnectRoom = async () => {
    log.info(`Disconnecting room`);
    audioTrackEnabledListener.current?.();
    videoTrackEnabledListener.current?.();

    const call = dailyCallRef.current;
    if (!call) {
      log.info('No call to disconnect');
      return;
    }

    dailyCallRef.current = null;
    call.setLocalVideo(false);
    call.setLocalAudio(false);
    await call.destroy();
  };

  const stopLocalTracks = () => {
    log.info(`Stopping local tracks`);
    // Do not stop the extra track, as it is an external entity
    // not created within this hook
    stopAllRef.current?.();

    audioTrackRef.current = null;
    extraAudioTrackRef.current = null;
    videoTrackRef.current = null;
    stopAllRef.current = null;
    localAvStreamRef.current = null;
  };

  const cleanup = useCallback(async () => {
    log.info(`Cleaning up`);
    stopLocalTracks();
    await disconnectRoom();
  }, []);

  const join = useCallback(
    async (url: string, token: string, participantName: string, userData?: any) => {
      log.info(`Joining room ${url}`);

      if (audioTrackRef.current) {
        log.info(`Other call already in progress, disconnecting...`);
        await disconnectRoom();
      }

      const call = Daily.createCallObject({
        url: url,
        token: token,
        subscribeToTracksAutomatically: true,
        audioSource: audioTrackRef.current || undefined,
        videoSource: videoTrackRef.current || undefined,
        startAudioOff: !isLocalAudioEnabled,
        startVideoOff: !isLocalVideoEnabled,
        dailyConfig: {
          avoidEval: true,
        },
      });
      dailyCallRef.current = call;
      await call.join({ userName: participantName, userData: userData });
      updateRoom();

      call.on('participant-updated', () => {
        updateRoom();
      });

      call.on('participant-joined', () => updateRoom());
      call.on('participant-left', () => updateRoom());
      call.on('track-started', e => {
        if (e.type === 'screenVideo' && e.participant?.user_id) {
          dispatch(setScreenShareInFocus(e.participant?.user_id));
          if (e?.participant?.local) {
            dispatch(setScreenSharing(true));
          }
        }

        updateRoom();
      });
      call.on('track-stopped', e => {
        if (e.type === 'screenVideo' && e.participant?.user_id) {
          dispatch(unsetScreenShareInFocus(e.participant?.user_id));

          if (e?.participant?.local) {
            dispatch(setScreenSharing(false));
          }
        }

        updateRoom();
      });
      call.on('joined-meeting', () => updateRoom());
      call.on('left-meeting', () => {
        cleanup();
        updateRoom();
      });
      call.on('error', () => updateRoom());
      call.on('active-speaker-change', activeSpeaker => {
        setActiveSpeaker(activeSpeaker?.activeSpeaker?.peerId);
      });
    },
    [cleanup, dispatch, isLocalAudioEnabled, isLocalVideoEnabled],
  );

  return {
    videoRoom: videoRoom,
    join: join,
    leave: cleanup,
    acquireStream: acquireStream,
    setLocalExtraAudioTrack: setLocalExtraAudioTrack,
    setLocalAudioTrack: setLocalAudioTrack,
    setLocalVideoTrack: setLocalVideoTrack,
    setLocalAudio: handleLocalAudioTrackChange,
    setLocalVideo: handleLocalVideoTrackChange,
    isLocalAudioEnabled: isLocalAudioEnabled,
    isLocalVideoEnabled: isLocalVideoEnabled,
    localAvStream: localAvStreamRef.current,
    dailyCall: dailyCallRef.current,
    activeSpeaker,
  };
}
